import React, { useState, useEffect, useRef } from 'react';
import { Lock, Delete, Loader2, TimerReset } from 'lucide-react';

interface PinEntryProps {
  secretId: string;
  onValidate: (pin: string) => Promise<boolean>;
  onSuccess: () => void;
}

const PIN_LENGTH = 4;
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 60_000;

interface LockoutState {
  count: number;
  lockedUntil: number | null;
}

const lockoutKey = (secretId: string) => `pinLockout_${secretId}`;

const readLockout = (secretId: string): LockoutState => {
  try {
    const raw = localStorage.getItem(lockoutKey(secretId));
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore parse errors, fall through to default
  }
  return { count: 0, lockedUntil: null };
};

const writeLockout = (secretId: string, state: LockoutState) => {
  localStorage.setItem(lockoutKey(secretId), JSON.stringify(state));
};

const PinEntry: React.FC<PinEntryProps> = ({ secretId, onValidate, onSuccess }) => {
  const [pin, setPin] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const [shaking, setShaking] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const validatingRef = useRef(false);

  // Load any existing lockout for this secretId on mount (this is a fast local
  // UX signal only — the real enforcement is the validate_pin RPC server-side,
  // so clearing localStorage or using a different device can't bypass it).
  useEffect(() => {
    const state = readLockout(secretId);
    if (state.lockedUntil && state.lockedUntil > Date.now()) {
      setLockedUntil(state.lockedUntil);
    }
  }, [secretId]);

  // Countdown ticker while locked out
  useEffect(() => {
    if (!lockedUntil) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        setLockedUntil(null);
        writeLockout(secretId, { count: 0, lockedUntil: null });
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil, secretId]);

  useEffect(() => {
    const validate = async () => {
      if (pin.length !== PIN_LENGTH || validatingRef.current || lockedUntil) return;
      validatingRef.current = true;
      setIsValidating(true);
      // Small delay for UX so user sees the last dot fill
      await new Promise(r => setTimeout(r, 100));

      const isValid = await onValidate(pin);
      setIsValidating(false);
      validatingRef.current = false;

      if (isValid) {
        writeLockout(secretId, { count: 0, lockedUntil: null });
        onSuccess();
      } else {
        const current = readLockout(secretId);
        const nextCount = current.count + 1;
        if (nextCount >= MAX_ATTEMPTS) {
          const until = Date.now() + LOCKOUT_MS;
          writeLockout(secretId, { count: nextCount, lockedUntil: until });
          setLockedUntil(until);
        } else {
          writeLockout(secretId, { count: nextCount, lockedUntil: null });
        }

        setError(true);
        setShaking(true);
        setTimeout(() => {
          setPin('');
          setError(false);
          setShaking(false);
        }, 500);
      }
    };

    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const handleNumClick = (num: string) => {
    if (pin.length < PIN_LENGTH && !isValidating && !lockedUntil) {
      setPin(prev => prev + num);
    }
  };

  const handleDelete = () => {
    if (!isValidating && !lockedUntil) {
      setPin(prev => prev.slice(0, -1));
    }
  };

  const isLocked = !!lockedUntil;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 p-4">
      <style>{`
        @keyframes pin-shake {
          10%, 90% { transform: translateX(-1px); }
          20%, 80% { transform: translateX(2px); }
          30%, 50%, 70% { transform: translateX(-4px); }
          40%, 60% { transform: translateX(4px); }
        }
        .pin-shake { animation: pin-shake 0.5s cubic-bezier(.36,.07,.19,.97) both; }
      `}</style>
      <div className={`flex flex-col items-center w-full max-w-xs ${shaking ? 'pin-shake' : ''}`}>
        {/* Brand */}
        <a href="https://m3tr.com" target="_blank" rel="noreferrer" className="flex items-center gap-2 mb-8 hover:opacity-80 transition-opacity">
          <img src="/m3tr-logo.png" alt="M3TR" className="w-8 h-8 rounded-full" />
          <span className="font-bold text-lg text-slate-100">M3TR</span>
        </a>

        <div className="mb-8 p-4 bg-slate-800 rounded-full border border-slate-700 shadow-xl shadow-cyan-900/20 relative">
          {isLocked ? (
            <TimerReset className="w-8 h-8 text-orange-400" />
          ) : isValidating ? (
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          ) : (
            <Lock className={`w-8 h-8 ${error ? 'text-red-500' : 'text-cyan-400'}`} />
          )}
        </div>

        <h2 className="text-xl font-semibold text-slate-100 mb-2">Acceso seguro</h2>

        {isLocked ? (
          <p className="text-slate-400 text-sm mb-8 text-center">
            Demasiados intentos. Vuelve a probar en <span className="text-orange-400 font-semibold">{secondsLeft}s</span>.
          </p>
        ) : (
          <p className="text-slate-400 text-sm mb-8 text-center">
            Ingresa tu PIN de acceso.
          </p>
        )}

        {/* Pin Dots */}
        <div className="flex gap-4 mb-10">
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full transition-all duration-300 ${
                i < pin.length
                  ? error ? 'bg-red-500 scale-110' : 'bg-cyan-400 scale-110'
                  : 'bg-slate-700'
              }`}
            />
          ))}
        </div>

        {/* Numpad */}
        <div className="grid grid-cols-3 gap-6 w-full mb-6">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              onClick={() => handleNumClick(num.toString())}
              disabled={isValidating || isLocked}
              className="h-16 w-16 mx-auto flex items-center justify-center rounded-full text-2xl font-medium text-slate-200 bg-slate-800 hover:bg-slate-700 active:bg-cyan-900 transition-colors border border-slate-700 disabled:opacity-40"
            >
              {num}
            </button>
          ))}
          <div className="col-span-1"></div>
          <button
            onClick={() => handleNumClick('0')}
            disabled={isValidating || isLocked}
            className="h-16 w-16 mx-auto flex items-center justify-center rounded-full text-2xl font-medium text-slate-200 bg-slate-800 hover:bg-slate-700 active:bg-cyan-900 transition-colors border border-slate-700 disabled:opacity-40"
          >
            0
          </button>
          <button
            onClick={handleDelete}
            disabled={isValidating || isLocked}
            className="h-16 w-16 mx-auto flex items-center justify-center rounded-full text-xl text-slate-400 hover:text-red-400 active:bg-slate-800 transition-colors disabled:opacity-40"
          >
            <Delete className="w-6 h-6" />
          </button>
        </div>

        <p className="text-slate-600 text-xs text-center">
          This device stays signed in — you won't need to re-enter your PIN next time.
        </p>
      </div>
    </div>
  );
};

export default PinEntry;
